import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
  Divider,
} from '@mui/material'
import { alpha, useTheme, type Theme } from '@mui/material/styles'
import { SmartToy, Save, Print } from '@mui/icons-material'
import { DEMO_CONTRACT, reviewContract, type RiskItem } from './dummyAi'

function renderHighlighted(
  text: string,
  items: RiskItem[],
  activeId: number | null,
  onPick: (id: number) => void,
  theme: Theme,
) {
  if (!items.length) return text
  const sorted = [...items].sort((a, b) => a.position.start - b.position.start)
  const parts: ReactNode[] = []
  let cursor = 0
  for (const item of sorted) {
    const { start, end } = item.position
    if (start > cursor) parts.push(<span key={`t-${cursor}`}>{text.slice(cursor, start)}</span>)
    const slice = text.slice(start, end)
    const isHigh = item.level === 'high'
    const active = activeId === item.id
    parts.push(
      <Box
        key={`r-${item.id}`}
        component="span"
        onClick={() => onPick(item.id)}
        sx={{
          textDecoration: 'underline',
          textDecorationColor: isHigh ? theme.palette.error.main : theme.palette.warning.main,
          textDecorationThickness: 2,
          bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : alpha(isHigh ? theme.palette.error.main : theme.palette.warning.main, 0.08),
          cursor: 'pointer',
        }}
      >
        {slice}
      </Box>,
    )
    cursor = end
  }
  if (cursor < text.length) parts.push(<span key={`t-end`}>{text.slice(cursor)}</span>)
  return <>{parts}</>
}

export function DraftingReviewTab() {
  const theme = useTheme()
  const [content, setContent] = useState(DEMO_CONTRACT)
  const [reviewing, setReviewing] = useState(false)
  const [riskItems, setRiskItems] = useState<RiskItem[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [suggestItem, setSuggestItem] = useState<RiskItem | null>(null)
  const [editDraft, setEditDraft] = useState(true)

  const stats = useMemo(() => {
    const high = riskItems.filter((r) => r.level === 'high').length
    const med = riskItems.filter((r) => r.level === 'medium').length
    const low = riskItems.filter((r) => r.level === 'low').length
    const ok = Math.max(0, 24 - high - med - low)
    return { high, med, low, ok }
  }, [riskItems])

  useEffect(() => {
    const t = setInterval(() => {
      try {
        localStorage.setItem('ecourt-draft-legal', content)
      } catch {
        /* ignore */
      }
    }, 30000)
    return () => clearInterval(t)
  }, [content])

  const runReview = async (text?: string) => {
    setReviewing(true)
    try {
      const r = await reviewContract(text ?? content)
      setRiskItems(r.riskItems)
      if (r.riskItems[0]) setActiveId(r.riskItems[0].id)
      setEditDraft(false)
    } finally {
      setReviewing(false)
    }
  }

  useEffect(() => {
    // Yêu cầu: vào tab là hiển thị kết quả rà soát luôn (mock).
    void runReview(DEMO_CONTRACT)
  }, [])

  const active = riskItems.find((x) => x.id === activeId)

  return (
    <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
      <Box sx={{ flex: '1 1 60%', minWidth: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', gap: 1 }}>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<SmartToy />}
            onClick={() => void runReview()}
            disabled={reviewing}
          >
            AI Rà soát
          </Button>
          <Button size="small" variant="outlined" startIcon={<Save />}>
            Lưu
          </Button>
          <Button size="small" variant="outlined" startIcon={<Print />}>
            In
          </Button>
        </Stack>
        {editDraft ? (
          <TextField
            multiline
            fullWidth
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Soạn thảo hoặc dán nội dung hợp đồng/văn bản cần rà soát..."
            sx={{
              flex: 1,
              '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', fontFamily: 'inherit' },
              '& .MuiInputBase-input': { height: '100% !important', overflow: 'auto !important' },
            }}
            InputProps={{
              sx: { p: 2, fontSize: '0.95rem', lineHeight: 1.75 },
            }}
          />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Đoạn gạch chân: rủi ro cao (đỏ) / cần xem xét (vàng). Nhấn đoạn để xem phân tích tương ứng.
              </Typography>
              <Button size="small" onClick={() => setEditDraft(true)}>
                Chỉnh sửa lại
              </Button>
            </Stack>
            <Paper variant="outlined" sx={{ flex: 1, p: 2, overflow: 'auto', borderRadius: 0 }}>
              <Typography
                variant="body2"
                component="div"
                sx={{
                  lineHeight: 1.75,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {renderHighlighted(content, riskItems, activeId, setActiveId, theme)}
              </Typography>
            </Paper>
            {active && (
              <Typography variant="caption" sx={{ mt: 1 }}>
                AI gợi ý sửa →{' '}
                <Button size="small" onClick={() => { setSuggestItem(active); setSuggestOpen(true); }}>
                  Xem
                </Button>
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          flex: '1 1 40%',
          minWidth: { xs: '100%', sm: 280 },
          maxWidth: { xs: '100%', md: 420 },
          borderLeft: { md: 1 },
          borderTop: { xs: 1, md: 0 },
          borderColor: 'divider',
          p: 2,
          overflow: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.secondary.main, 0.12),
              color: theme.palette.secondary.dark,
              flexShrink: 0,
            }}
          >
            <SmartToy fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
              AI Rà soát nội dung
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              Tổng quan rủi ro & gợi ý chỉnh điều khoản
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Chip size="small" label={`Tổng điều khoản: 24`} variant="outlined" />
          <Chip
            size="small"
            label={`Rủi ro cao: ${stats.high}`}
            sx={{ bgcolor: alpha(theme.palette.error.main, 0.10), color: theme.palette.error.dark, borderColor: alpha(theme.palette.error.main, 0.35) }}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`Cần xem xét: ${stats.med}`}
            sx={{
              bgcolor: alpha(theme.palette.warning.main, 0.14),
              color: theme.palette.warning.dark,
              borderColor: alpha(theme.palette.warning.main, 0.38),
            }}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`Ổn: ${stats.ok}`}
            sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.dark, borderColor: alpha(theme.palette.success.main, 0.35) }}
            variant="outlined"
          />
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            borderRadius: 2,
            bgcolor: 'background.paper',
            borderColor: alpha(theme.palette.divider, 0.8),
            mb: 1.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Điều khoản rủi ro
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            Nhấn vào từng mục để đồng bộ highlight ở văn bản.
          </Typography>
        </Paper>

        {!riskItems.length && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderColor: alpha(theme.palette.primary.main, 0.18),
            }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
              Chưa có kết quả rà soát
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Nhấn <b>AI Rà soát</b> để quét văn bản. Mỗi gợi ý có thể xem so sánh và “Áp dụng” theo cơ chế human-in-the-loop.
            </Typography>
          </Paper>
        )}
        <Stack spacing={2}>
          {riskItems.map((r) => (
            <Paper
              key={r.id}
              variant="outlined"
              onClick={() => setActiveId(r.id)}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                borderRadius: 2,
                borderColor:
                  activeId === r.id
                    ? 'primary.main'
                    : r.level === 'high'
                      ? alpha(theme.palette.error.main, 0.35)
                      : r.level === 'medium'
                        ? alpha(theme.palette.warning.main, 0.35)
                        : alpha(theme.palette.divider, 0.9),
                bgcolor:
                  activeId === r.id
                    ? alpha(theme.palette.primary.main, 0.08)
                    : r.level === 'high'
                      ? alpha(theme.palette.error.main, 0.04)
                      : r.level === 'medium'
                        ? alpha(theme.palette.warning.main, 0.05)
                        : 'background.paper',
                transition: 'background-color 120ms ease, border-color 120ms ease',
                '&:hover': {
                  bgcolor:
                    activeId === r.id
                      ? alpha(theme.palette.primary.main, 0.10)
                      : r.level === 'high'
                        ? alpha(theme.palette.error.main, 0.06)
                        : r.level === 'medium'
                          ? alpha(theme.palette.warning.main, 0.07)
                          : alpha(theme.palette.grey[500], 0.03),
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.75 }}>
                <Chip
                  size="small"
                  label={r.level === 'high' ? 'Cao' : r.level === 'medium' ? 'Trung bình' : 'Thấp'}
                  sx={{
                    height: 22,
                    fontWeight: 800,
                    bgcolor:
                      r.level === 'high'
                        ? alpha(theme.palette.error.main, 0.12)
                        : r.level === 'medium'
                          ? alpha(theme.palette.warning.main, 0.18)
                          : alpha(theme.palette.info.main, 0.12),
                    color:
                      r.level === 'high'
                        ? theme.palette.error.dark
                        : r.level === 'medium'
                          ? theme.palette.warning.dark
                          : theme.palette.info.dark,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.25 }}>
                    {r.clause} · {r.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {r.issue}
                  </Typography>
                </Box>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  borderLeft: `3px solid ${alpha(r.level === 'high' ? theme.palette.error.main : theme.palette.warning.main, 0.8)}`,
                  pl: 1.25,
                }}
              >
                &quot;{r.original}&quot;
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setSuggestItem(r); setSuggestOpen(true); }}>
                  Gợi ý sửa
                </Button>
                <Button size="small" onClick={(e) => e.stopPropagation()}>
                  Bỏ qua
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Paper>

      <Dialog open={suggestOpen} onClose={() => setSuggestOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle>So sánh gợi ý</DialogTitle>
        <DialogContent>
          {suggestItem && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Paper sx={{ flex: 1, p: 2, bgcolor: alpha(theme.palette.error.main, 0.06), borderRadius: 0 }}>
                <Typography variant="caption" fontWeight={600}>
                  VĂN BẢN GỐC
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {suggestItem.original}
                </Typography>
              </Paper>
              <Paper sx={{ flex: 1, p: 2, bgcolor: alpha(theme.palette.success.main, 0.08), borderRadius: 0 }}>
                <Typography variant="caption" fontWeight={600}>
                  AI GỢI Ý
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {suggestItem.suggestion}
                </Typography>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuggestOpen(false)}>Giữ nguyên</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!suggestItem) return
              const { start, end } = suggestItem.position
              setContent((prev) => prev.slice(0, start) + suggestItem.suggestion + prev.slice(end))
              setSuggestOpen(false)
            }}
          >
            Áp dụng gợi ý
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
